import React, { useState, useEffect } from 'react';
import { TrendingDown, DollarSign, PieChart, BarChart3, Calendar, Download, Filter, RefreshCw } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Card } from '../../../components/ui/card';
import { useToast } from '../../../components/ui/use-toast';
import { PieChart as RechartsPie, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import axios from 'axios';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#00B4D8', '#0096c7', '#0077B6', '#023E8A', '#03045E'];

const ProfitReport = () => {
  const { toast } = useToast();
  
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);
    
    setEndDate(format(today, 'yyyy-MM-dd'));
    setStartDate(format(thirtyDaysAgo, 'yyyy-MM-dd'));
  }, []);
  
  const fetchReport = async () => {
    if (!startDate || !endDate) {
      toast({
        variant: "destructive",
        title: "Missing Dates",
        description: "Please select start and end dates",
      });
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(
        `${API_URL}/api/finance/reports/profit?start_date=${startDate}&end_date=${endDate}&currency=${currency}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setReportData(response.data);
      
      toast({
        title: "Report Generated",
        description: "Profit report generated successfully",
      });
    } catch (error) {
      console.error('Error fetching profit report:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.response?.data?.detail || "Failed to generate report",
      });
    } finally {
      setLoading(false);
    }
  };
  
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(value);
  };
  
  const formatPieData = () => {
    if (!reportData?.cost_breakdown) return [];
    return reportData.cost_breakdown.map(item => ({
      name: item.category,
      value: parseFloat(item.amount)
    }));
  };
  
  const formatBarData = () => {
    if (!reportData?.revenue_vs_profit_by_month) return [];
    return reportData.revenue_vs_profit_by_month.map(item => ({
      month: item.month,
      revenue: item.revenue,
      costs: item.costs,
      profit: item.profit
    }));
  };
  
  const handleExport = async (format) => {
    if (!reportData) {
      toast({
        variant: "destructive",
        title: "No Report Data",
        description: "Please generate a report first",
      });
      return;
    }
    
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(
        `${API_URL}/api/finance/reports/profit/export/${format}?start_date=${startDate}&end_date=${endDate}&currency=${currency}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const extension = format === 'pdf' ? 'pdf' : format === 'excel' ? 'xlsx' : 'csv';
      link.setAttribute('download', `profit_report_${startDate}_${endDate}.${extension}`);
      
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast({
        title: "Export Successful",
        description: `Profit report exported as ${format.toUpperCase()}`,
      });
    } catch (error) {
      console.error(`Error exporting to ${format}:`, error);
      toast({
        variant: "destructive",
        title: "Export Failed",
        description: error.response?.data?.detail || `Failed to export report as ${format}`,
      });
    }
  };
  
  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-[#2B2D42] flex items-center gap-3">
          <TrendingDown className="w-8 h-8 text-[#00B4D8]" />
          Profit Report
        </h1>
        <p className="text-[#6C757D] mt-2">
          Revenue, costs, and profit margin analysis
        </p>
      </div>
      
      {/* Filters */}
      <Card className="p-6 mb-6 border-[#E9ECEF]">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-[#6C757D]" />
          <h2 className="text-lg font-semibold text-[#2B2D42]">Filters</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-[#2B2D42] mb-2">
              <Calendar className="w-4 h-4 inline mr-1" />
              Start Date
            </label>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-[#2B2D42] mb-2">
              <Calendar className="w-4 h-4 inline mr-1" />
              End Date
            </label>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-[#2B2D42] mb-2">
              <DollarSign className="w-4 h-4 inline mr-1" />
              Currency
            </label>
            <Select value={currency} onValueChange={setCurrency}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="USD">USD</SelectItem>
                <SelectItem value="EUR">EUR</SelectItem>
                <SelectItem value="GBP">GBP</SelectItem>
                <SelectItem value="BRL">BRL</SelectItem>
                <SelectItem value="JPY">JPY</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex items-end">
            <Button
              onClick={fetchReport}
              disabled={loading}
              className="w-full bg-[#00B4D8] hover:bg-[#0096c7] text-white"
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Generating...
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <BarChart3 className="w-4 h-4" />
                  Generate Report
                </div>
              )}
            </Button>
          </div>
        </div>
      </Card>
      
      {reportData && (
        <>
          {/* Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <Card className="p-6 border-[#E9ECEF] hover:shadow-lg transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-[#6C757D]">Total Revenue</span>
                <DollarSign className="w-5 h-5 text-[#00B4D8]" />
              </div>
              <p className="text-3xl font-bold text-[#2B2D42]">
                {formatCurrency(reportData.metrics.total_revenue)}
              </p>
            </Card>
            
            <Card className="p-6 border-[#E9ECEF] hover:shadow-lg transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-[#6C757D]">Total Costs</span>
                <TrendingDown className="w-5 h-5 text-red-500" />
              </div>
              <p className="text-3xl font-bold text-red-600">
                {formatCurrency(reportData.metrics.total_costs)}
              </p>
            </Card>
            
            <Card className="p-6 border-[#E9ECEF] hover:shadow-lg transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-[#6C757D]">Gross Profit</span>
                <DollarSign className="w-5 h-5 text-green-600" />
              </div>
              <p className={`text-3xl font-bold ${reportData.metrics.gross_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(reportData.metrics.gross_profit)}
              </p>
            </Card>
            
            <Card className="p-6 border-[#E9ECEF] hover:shadow-lg transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-[#6C757D]">Profit Margin</span>
                <PieChart className="w-5 h-5 text-[#00B4D8]" />
              </div>
              <p className={`text-3xl font-bold ${reportData.metrics.profit_margin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {reportData.metrics.profit_margin.toFixed(1)}%
              </p>
            </Card>
          </div>
          
          {/* Charts Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Cost Breakdown Pie Chart */}
            {reportData.cost_breakdown.length > 0 && (
              <Card className="p-6 border-[#E9ECEF]">
                <h3 className="text-lg font-semibold text-[#2B2D42] mb-4 flex items-center gap-2">
                  <PieChart className="w-5 h-5 text-[#00B4D8]" />
                  Cost Breakdown by Category
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <RechartsPie>
                    <Pie
                      data={formatPieData()}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({name, percent}) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {formatPieData().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                  </RechartsPie>
                </ResponsiveContainer>
                
                {/* Legend */}
                <div className="mt-4 grid grid-cols-2 gap-2">
                  {reportData.cost_breakdown.map((item, index) => (
                    <div key={item.category} className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded" 
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      />
                      <span className="text-sm text-[#6C757D]">
                        {item.category}: {formatCurrency(item.amount)}
                      </span>
                    </div>
                  ))}
                </div>
              </Card>
            )}
            
            {/* Revenue vs Profit Bar Chart */}
            {reportData.revenue_vs_profit_by_month.length > 0 && (
              <Card className="p-6 border-[#E9ECEF]">
                <h3 className="text-lg font-semibold text-[#2B2D42] mb-4 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-[#00B4D8]" />
                  Revenue vs Costs vs Profit
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={formatBarData()}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E9ECEF" />
                    <XAxis 
                      dataKey="month" 
                      stroke="#6C757D"
                      style={{ fontSize: '12px' }}
                    />
                    <YAxis 
                      stroke="#6C757D"
                      style={{ fontSize: '12px' }}
                      tickFormatter={(value) => `${currency} ${value.toLocaleString()}`}
                    />
                    <Tooltip 
                      formatter={(value) => formatCurrency(value)}
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #E9ECEF',
                        borderRadius: '8px'
                      }}
                    />
                    <Legend />
                    <Bar dataKey="revenue" fill="#00B4D8" name="Revenue" />
                    <Bar dataKey="costs" fill="#EF4444" name="Costs" />
                    <Bar dataKey="profit" fill="#10B981" name="Profit" />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            )}
          </div>
          
          {/* Export */}
          <Card className="p-6 border-[#E9ECEF]">
            <h3 className="text-lg font-semibold text-[#2B2D42] mb-4">Export Report</h3>
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex items-center gap-2"
                onClick={() => handleExport('pdf')}
              >
                <Download className="w-4 h-4" />
                Export PDF
              </Button>
              <Button
                variant="outline"
                className="flex items-center gap-2"
                onClick={() => handleExport('excel')}
              >
                <Download className="w-4 h-4" />
                Export Excel
              </Button>
              <Button
                variant="outline"
                className="flex items-center gap-2"
                onClick={() => handleExport('csv')}
              >
                <Download className="w-4 h-4" />
                Export CSV
              </Button>
            </div>
          </Card>
        </>
      )}
      
      {!reportData && !loading && (
        <Card className="p-12 text-center border-[#E9ECEF]">
          <BarChart3 className="w-16 h-16 text-[#6C757D] mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-[#2B2D42] mb-2">
            No Report Generated
          </h3>
          <p className="text-[#6C757D] mb-6">
            Select dates and click "Generate Report" to view profit analysis
          </p>
        </Card>
      )}
    </div>
  );
};

export default ProfitReport;
